import {
    type BoxShadowValue,
    type ViewStyle,
    StyleSheet,
    Platform
} from "react-native";
import {
    type BadgeIndicatorDirectionConstantType,
    type BadgeIndicatorPositionConstantType,
    type BadgeIndicatorHitAreaConstantType,
    type BadgeIndicatorColorsConstantType,
    type BadgeIndicatorCountConstantType,
    type BadgeIndicatorMarkConstantType,
    type BadgeIndicatorRingConstantType,
    type BadgeIndicatorDynamicStyleType,
    type BadgeIndicatorColors,
    type BadgeIndicatorMark
} from "./type";
import {
    DISABLED_COLOR_ROLES,
    getColorRoleValue,
    getTypeColors
} from "../../../../helpers/theme/typeColors";
import type {
    HitAreaMeasures,
    Mutable
} from "../../../../types";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    formatNumber
} from "../../../../helpers/locale/format";
import {
    getHitArea
} from "../../../../helpers/theme/control";
import {
    getHugStyle
} from "../../../../helpers/theme/spread";
import {
    getOpaqueColor,
    webStyle
} from "../../../../utils";

export const BADGE_INDICATOR_MAX_COUNT = 99;

export const getBadgeIndicatorMark = ({
    isLoading,
    isVisible,
    count
}: BadgeIndicatorMarkConstantType): BadgeIndicatorMark => {
    if(!isVisible) {
        return "none";
    }

    if(isLoading) {
        return "loading";
    }

    if(count === undefined) {
        return "dot";
    }

    if(Number.isNaN(count) || count <= 0) {
        return "none";
    }

    return "count";
};

export const getBadgeIndicatorCount = ({
    max = BADGE_INDICATOR_MAX_COUNT,
    locale,
    count
}: BadgeIndicatorCountConstantType): string => {
    if(count > max) {
        return `${formatNumber({
            value: max,
            locale
        })}+`;
    }

    return formatNumber({
        options: {
            maximumFractionDigits: 0
        },
        value: count,
        locale
    });
};

export const getBadgeIndicatorColors = ({
    isDisabled,
    colors,
    type
}: BadgeIndicatorColorsConstantType): BadgeIndicatorColors => {
    const typeColors = getTypeColors({
        variant: "filled",
        isDisabled,
        colors,
        type
    });

    if(!isDisabled) {
        return {
            containerColor: typeColors.containerColor,
            borderColor: typeColors.borderColor,
            titleColor: typeColors.titleColor
        };
    }

    return {
        borderColor: getColorRoleValue({
            role: DISABLED_COLOR_ROLES.outline.borderColor,
            colors
        }),
        containerColor: getOpaqueColor({
            surface: colors.system.default.background,
            color: typeColors.containerColor
        }),
        titleColor: typeColors.titleColor
    };
};

export const getBadgeIndicatorPosition = ({
    anchorInset,
    location,
    markSize
}: BadgeIndicatorPositionConstantType): ViewStyle => {
    const position: Mutable<ViewStyle> = {};

    if(location) {
        position.start = location.start;
        position.end = location.end;
        position.bottom = location.bottom;
        position.top = location.top;

        return position;
    }

    const offset = (anchorInset ?? 0) - markSize / 2;

    position.end = offset;
    position.top = offset;

    return position;
};

export const getBadgeIndicatorDirection = ({
    direction
}: BadgeIndicatorDirectionConstantType): ViewStyle => {
    if(Platform.OS === "web") {
        return {};
    }

    return {
        direction
    };
};

export const getBadgeIndicatorRing = ({
    currentColors,
    floorColor,
    isDisabled,
    borders
}: BadgeIndicatorRingConstantType): Array<BoxShadowValue> => {
    const ring: Array<BoxShadowValue> = [
        {
            spreadDistance: borders.subtract,
            color: floorColor,
            offsetX: 0,
            offsetY: 0
        }
    ];

    if(isDisabled) {
        ring.push({
            color: currentColors.borderColor,
            spreadDistance: borders.line,
            inset: true,
            offsetX: 0,
            offsetY: 0
        });
    }

    return ring;
};

export const getBadgeIndicatorHitArea = ({
    touchTargets,
    pointerType
}: BadgeIndicatorHitAreaConstantType): HitAreaMeasures => {
    return getHitArea({
        visualSize: 0,
        touchTargets,
        pointerType
    });
};

const stylesheet = StyleSheet.create({
    container: {
        justifyContent: "center",
        alignItems: "center",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    mark: {
        justifyContent: "center",
        position: "absolute",
        flexDirection: "row",
        alignItems: "center"
    },
    surface: {
        position: "relative"
    },
    title: {
        textAlign: "center",
        fontVariant: [
            "tabular-nums"
        ]
    }
});

export const useStyles = ({
    floorBackgroundColor,
    spreadBehaviour,
    isFocusVisible,
    currentColors,
    shapeConfigs,
    titleVariant,
    touchTargets,
    anchorInset,
    isPressable,
    pointerType,
    isDisabled,
    typography,
    direction,
    isLoading,
    userStyle,
    location,
    radiuses,
    borders,
    colors,
    spaces,
    mark
}: BadgeIndicatorDynamicStyleType) => {
    const hitArea = getBadgeIndicatorHitArea({
        touchTargets,
        pointerType
    });

    const countSize = typography[titleVariant].lineHeight;

    const styles = {
        mark: {
            boxShadow: getBadgeIndicatorRing({
                floorColor: colors.content.container[floorBackgroundColor],
                currentColors,
                isDisabled,
                borders
            }),
            backgroundColor: currentColors.containerColor,
            paddingHorizontal: spaces.spacingXs,
            borderRadius: radiuses.full,
            minHeight: countSize,
            minWidth: countSize
        } as Mutable<ViewStyle>,
        container: {
            minHeight: hitArea.minHeight,
            minWidth: hitArea.minWidth
        } as Mutable<ViewStyle>,
        surface: {
        } as Mutable<ViewStyle>
    };

    Object.assign(styles.mark, getBadgeIndicatorPosition({
        markSize: mark === "dot" ? spaces.spacingSm : countSize,
        anchorInset,
        location
    }));

    Object.assign(styles.surface, getBadgeIndicatorDirection({
        direction
    }));

    if(mark === "dot") {
        styles.mark.minHeight = spaces.spacingSm;
        styles.mark.minWidth = spaces.spacingSm;
        styles.mark.height = spaces.spacingSm;
        styles.mark.width = spaces.spacingSm;
    }

    if(mark === "dot" || mark === "loading") {
        delete styles.mark.paddingHorizontal;
    }

    if(isPressable) {
        Object.assign(styles.surface, getCornerStyle({
            radius: radiuses.actions,
            shapeConfigs
        }));

        Object.assign(styles.surface, getFocusRingStyle({
            colorKey: "default",
            isFocusVisible,
            borders,
            colors
        }));
    }

    if(isPressable && isLoading) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    const spreadStyle = isPressable ? styles.container : styles.surface;

    if(spreadBehaviour === "baseline") {
        Object.assign(spreadStyle, getHugStyle({
            style: userStyle
        }));
    }

    if(spreadBehaviour === "stretch") {
        spreadStyle.alignSelf = spreadBehaviour;
        spreadStyle.flexShrink = 1;
        spreadStyle.width = "100%";
        styles.surface.alignSelf = spreadBehaviour;
    }

    return styles;
};
export default stylesheet;
